/**
 * The reducer: (state, action, rng) -> state (HANDOFF §4.1 / §4.4).
 *
 * The single entry point for evolving the simulation. PURE: no rendering, no
 * DOM, no globals, no direct randomness (rng injected). Render reads the returned
 * state; input emits the {@link Action}s.
 *
 * Agent A (sim foundation) owns this file. It implements the DETERMINISTIC parts:
 * match layout, hand dealing, the `Tick` clock/energy/timer/wind/swing-meter
 * bookkeeping, and the action plumbing. Shot resolution, physics, scoring, card
 * effects, and AI are delegated to Agents B and C through their typed signatures
 * (each call site is marked `TODO(Agent B/C)`); those throwing stubs are the only
 * thing standing between this skeleton and a playable match.
 *
 * No magic numbers: every constant comes from ./constants.ts.
 */
import type {
  Action,
  Ball,
  Club,
  DefenseCard,
  Difficulty,
  Fairway,
  GameState,
  Green,
  ScoreResult,
  ShotParams,
  Side,
  SideState,
  SwingState,
  TeeBox,
  TeeBoxKey,
  TeeBoxSet,
  Vec2,
} from './types.ts';
import {
  AI_INITIAL,
  AI_PARAMS,
  DECK_COPIES,
  DEFAULT_DIFFICULTY,
  DEFENSE_CHARGE,
  GOPHER,
  ENERGY_REGEN_PER_SECOND,
  FAIRWAY,
  FIELD,
  GREEN,
  HAND_SIZE,
  SHOT_CLOCK,
  SLOPE_MAG_BASE,
  SLOPE_MAG_RANGE,
  SWING,
  TEE_BOX,
  TEE_KEYS,
  boxForPower,
} from './constants.ts';
import { CLUBS } from './clubs.ts';
import { makeFairwayShape, makeGreenShape } from './greens.ts';
import { DEFENSE_CARDS, applyDefense } from './cards.ts';
import { resolveSwing, type SwingInput } from './swing.ts';
import { createBall, stepBall } from './physics.ts';
import { hazardKindAt, scoreShot } from './scoring.ts';
import { BALL_REST_SECONDS, BALL_SINK_SECONDS, STROKE_DELTA } from './constants.ts';
import { decideAi, attackArmed, defenseArmed } from './ai.ts';
import { resolveConfig, type GameConfig } from './config.ts';
import type { Rng } from './types.ts';

const TWO_PI = Math.PI * 2;

// ---------------------------------------------------------------------------
// Math helpers (pure)
// ---------------------------------------------------------------------------

function vec(x: number, y: number): Vec2 {
  return { x, y };
}

function dist(a: Vec2, b: Vec2): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

// ---------------------------------------------------------------------------
// Deterministic deck shuffle (Fisher–Yates with injected rng) — §4.3
// ---------------------------------------------------------------------------

function shuffle<T>(items: readonly T[], rng: Rng): T[] {
  const a = items.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const ai = a[i] as T;
    const aj = a[j] as T;
    a[i] = aj;
    a[j] = ai;
  }
  return a;
}

function buildDeck<T>(cards: readonly T[], rng: Rng): T[] {
  const stacked: T[] = [];
  for (let copy = 0; copy < DECK_COPIES; copy++) stacked.push(...cards);
  return shuffle(stacked, rng);
}

/** Draw `n` cards off the top of a deck; returns the hand and the remaining deck. */
function deal<T>(deck: readonly T[], n: number): { hand: T[]; rest: T[] } {
  const rest = deck.slice();
  const hand: T[] = [];
  for (let i = 0; i < n; i++) {
    const card = rest.pop();
    if (card !== undefined) hand.push(card);
  }
  return { hand, rest };
}

// ---------------------------------------------------------------------------
// Field layout (§3.1 / §3.2) — pure, seeded
// ---------------------------------------------------------------------------

const W = FIELD.width;
const H = FIELD.height;

function greenRadius(): number {
  return Math.min(W * GREEN.radiusWidthFactor, GREEN.radiusMax);
}

function makeGreen(
  center: Vec2,
  radius: number,
  pinAngle: number,
  slopeAngle: number,
  slopeMag: number,
  shape: { rx: number; ry: number; rot: number; lobe: number; lobePhase: number },
): Green {
  const pinDist = radius * GREEN.pinOffsetFactor;
  return {
    center,
    radius,
    pin: vec(center.x + Math.cos(pinAngle) * pinDist, center.y + Math.sin(pinAngle) * pinDist),
    slopeAngle,
    slopeMag,
    hazards: [],
    rx: shape.rx,
    ry: shape.ry,
    rot: shape.rot,
    lobe: shape.lobe,
    lobePhase: shape.lobePhase,
  };
}

/**
 * Build a side's three tee boxes (§3.1), fanning from its own green toward the
 * target/mid-field. `toTargetY` is the vertical lean (−1 for `you`, own green at
 * the bottom; +1 for `rival`, own green at the top). `toCenterX` is the horizontal
 * lean toward the field centre so the two sides are 180° mirror-opposites (diagonal).
 * Distance-to-target ordering SHORT < MID < LONG holds by design; a card launches from
 * the tee matching its POWER (see {@link boxForPower}).
 */
function makeTees(green: Green, toTargetY: number, toCenterX: number): TeeBoxSet {
  const radius = green.radius;
  const baseX = green.center.x + toCenterX * (radius + W * TEE_BOX.longSideFactor);
  const baseY = green.center.y;
  const longBack = radius * TEE_BOX.longBackFactor; // LONG only: push to the container rear edge
  const forward = Math.min(H * TEE_BOX.forwardFactor, TEE_BOX.forwardMax);
  const side = W * TEE_BOX.sideStepFactor;
  const stepFor: Readonly<Record<TeeBoxKey, number>> = {
    long: 0,
    mid: 1,
    short: 1 + TEE_BOX.shortSpacingMult,
  };

  const make = (key: TeeBoxKey): TeeBox => {
    const s = stepFor[key];
    const back = key === 'long' ? longBack : 0;
    return {
      key,
      pos: vec(baseX - toCenterX * side * s, baseY - toTargetY * back + toTargetY * forward * s),
      roughRemaining: 0,
    };
  };

  return { short: make('short'), mid: make('mid'), long: make('long') };
}

/**
 * Build a side's FAIRWAY (§3.1) from its MID & SHORT tees + the SHARED shape params, and
 * re-centre those two tees onto the fairway's medial axis (an elongated rotated-ellipse
 * strip). Both sides feed point-symmetric tees + the same `shape`, so the two fairways come
 * out mirror-identical.
 */
function buildFairway(
  tees: TeeBoxSet,
  shape: { e: number; lobe: number; lobePhase: number; jitter: number },
): { tees: TeeBoxSet; fairway: Fairway } {
  const mid = tees.mid.pos;
  const short = tees.short.pos;
  const center = vec((mid.x + short.x) / 2, (mid.y + short.y) / 2);
  const half = dist(mid, short) / 2;
  const radius = half * FAIRWAY.lengthPad;
  const rot = Math.atan2(short.y - mid.y, short.x - mid.x) + shape.jitter;
  const fairway: Fairway = {
    center,
    radius,
    rx: radius,
    ry: radius / shape.e,
    rot,
    lobe: shape.lobe,
    lobePhase: shape.lobePhase,
  };

  const sinR = Math.sin(rot);
  const onAxisX = (y: number): number =>
    Math.abs(sinR) < 1e-4 ? center.x : center.x + ((y - center.y) / sinR) * Math.cos(rot);

  const adjusted: TeeBoxSet = {
    long: tees.long,
    mid: { ...tees.mid, pos: vec(onAxisX(mid.y), mid.y) },
    short: { ...tees.short, pos: vec(onAxisX(short.y), short.y) },
  };
  return { tees: adjusted, fairway };
}

function makeSide(
  green: Green,
  fairway: Fairway,
  tees: TeeBoxSet,
  offense: readonly Club[], // the chosen clubs (one per distance) — held as-is, never cycled
  defenseDeck: readonly DefenseCard[], // shuffled defense draw pile
  defenseList: readonly DefenseCard[], // the chosen defense cards (reshuffles from these only)
  cfg: GameConfig, // tunable starting economy
): SideState {
  const def = deal(defenseDeck, HAND_SIZE);
  return {
    energy: cfg.energyStart,
    score: 0,
    shots: 0,
    green,
    fairway,
    tees,
    offenseHand: offense,
    defenseHand: def.hand,
    defenseDeck: def.rest,
    defenseList,
    playoffDp: null,
    gopherPending: false,
    shotClock: SHOT_CLOCK.seconds,
    defenseCharges: cfg.defenseChargeStart,
  };
}

/** One club at each distance (long/mid/short) from a roster — the default offense set + the
 *  AI's hand. The deck builder hands the player exactly one per distance directly. */
function onePerDistance(cards: readonly Club[]): Club[] {
  const out: Club[] = [];
  for (const box of ['long', 'mid', 'short'] as const) {
    const c = cards.find((card) => boxForPower(card.power) === box);
    if (c) out.push(c);
  }
  return out;
}

export const idleSwing: SwingState = {
  phase: 'idle',
  side: 'you',
  clubIdx: null,
  meter: 0,
  meterDir: 1,
  aimAngle: null,
  backswingMeter: null,
  impactMeter: null,
};

/**
 * Construct a fresh, laid-out game state from a seed (§4.4). All layout
 * randomness (pin angle, slope) is drawn from the injected `rng`, so the same
 * seed yields the same match.
 */
export function createInitialState(
  rng: Rng,
  difficulty: Difficulty = DEFAULT_DIFFICULTY,
  seed = 0,
  /** The player's chosen deck (deck builder); defaults to the full roster. The AI always
   *  draws from the full roster. */
  playerDeck?: { offense: readonly Club[]; defense: readonly DefenseCard[] },
  /** Tunable balance overrides (remote config). Omitted ⇒ compiled defaults (byte-identical). */
  config?: Partial<GameConfig>,
): GameState {
  const cfg = resolveConfig(config);
  const radius = greenRadius();

  // Diagonal layout: you bottom-right, rival the 180° point-reflection (top-left). Each side's
  // 3 tees fan to the inside-LEFT of its green on a mirrored fairway; a card launches from the
  // tee matching its POWER. Both greens share ONE shape + a mirrored slope + a mirrored pin, so
  // both players face the IDENTICAL green — a fair duel.
  const youCenter = vec(W * GREEN.centerXFactor, H * GREEN.youCenterYFactor);
  const rivalCenter = vec(W - youCenter.x, H - youCenter.y);

  // Deterministic draw order (fixed for replay): pinAngle → slope → shape (one each, shared).
  // A point-reflection of an ellipse+lobe is itself, so the SAME params give mirror-identical
  // greens; the pin + slope are reflected (+π) for the rival.
  const pinAngle = rng() * TWO_PI;
  const slopeAngle = rng() * TWO_PI;
  const slopeMag = SLOPE_MAG_BASE + rng() * SLOPE_MAG_RANGE;
  const shape = makeGreenShape(radius, rng);

  const youGreen = makeGreen(youCenter, radius, pinAngle, slopeAngle, slopeMag, shape);
  const rivalGreen = makeGreen(rivalCenter, radius, pinAngle + Math.PI, slopeAngle + Math.PI, slopeMag, shape);

  const youTees = makeTees(youGreen, -1, -1);
  const rivalTees = makeTees(rivalGreen, +1, +1);

  // Offense: one club per distance, held as-is (no shuffle/cycle) — the player's picks, or a
  // default one-per-distance set for the AI / when no deck was chosen.
  const youOffense = playerDeck?.offense ?? onePerDistance(CLUBS);
  const rivalOffense = onePerDistance(CLUBS);
  // Defense: a shuffled draw pile built ONLY from the chosen cards (reshuffles from the same).
  const youDefenseList = playerDeck?.defense ?? DEFENSE_CARDS;
  const youDefDeck = buildDeck(youDefenseList, rng);
  const rivalDefDeck = buildDeck(DEFENSE_CARDS, rng);

  // ONE shared fairway shape (drawn LAST so the rng stream above is unchanged). Both sides
  // build from point-symmetric tees + the same params → mirror-identical fairways.
  const fairwayShape = makeFairwayShape(rng);
  const youLayout = buildFairway(youTees, fairwayShape);
  const rivalLayout = buildFairway(rivalTees, fairwayShape);

  return {
    clock: cfg.matchDuration,
    phase: 'playing',
    mode: 'duel', // the range mode is set by the caller (main.ts) post-construction
    winner: null,
    maxEnergy: cfg.energyMax,
    you: makeSide(youGreen, youLayout.fairway, youLayout.tees, youOffense, youDefDeck, youDefenseList, cfg),
    rival: makeSide(rivalGreen, rivalLayout.fairway, rivalLayout.tees, rivalOffense, rivalDefDeck, DEFENSE_CARDS, cfg),
    balls: [],
    wind: { remaining: 0, accel: vec(0, 0) },
    swing: idleSwing,
    rivalSwing: idleSwing,
    aiRival: true, // solo by default; the PvP match host sets this false (rival = a human)
    ai: {
      difficulty,
      attackTimer: AI_INITIAL.attackTimer,
      defenseTimer: AI_INITIAL.defenseTimer,
    },
    seed,
  };
}

// ---------------------------------------------------------------------------
// Small immutable updaters
// ---------------------------------------------------------------------------

function withSide(state: GameState, side: Side, next: SideState): GameState {
  return side === 'you' ? { ...state, you: next } : { ...state, rival: next };
}

/** That side's swing meter (`you` → state.swing, `rival` → state.rivalSwing). */
function swingOf(state: GameState, side: Side): SwingState {
  return side === 'you' ? state.swing : state.rivalSwing;
}
/** Replace that side's swing meter (concurrent per-side swings — both sides can swing at once). */
function withSwing(state: GameState, side: Side, sw: SwingState): GameState {
  return side === 'you' ? { ...state, swing: sw } : { ...state, rivalSwing: sw };
}

function regenEnergy(side: SideState, dt: number, perSecond: number, max: number): SideState {
  const energy = Math.min(max, side.energy + dt * perSecond);
  return energy === side.energy ? side : { ...side, energy };
}

/** Decay each roughed TEE's debuff timer toward 0 (§3.7 — rough is per-tee: a Rough card fouls
 *  ONE launch spot, not the whole side). Returns the same object if nothing was roughed. */
function tickTeeRough(side: SideState, dt: number): SideState {
  let changed = false;
  const tees = { ...side.tees };
  for (const key of TEE_KEYS) {
    const t = tees[key];
    if (t.roughRemaining > 0) {
      tees[key] = { ...t, roughRemaining: Math.max(0, t.roughRemaining - dt) };
      changed = true;
    }
  }
  return changed ? { ...side, tees } : side;
}

/** The cheapest offense card a side holds (Infinity if it holds none). */
function cheapestOffenseCost(side: SideState): number {
  return side.offenseHand.reduce((m, c) => Math.min(m, c.cost), Infinity);
}

/**
 * Advance a side's SHOT CLOCK (anti-turtle, §3.6 playtest). It only runs while the side can
 * AFFORD a club (never penalise being broke); on expiry it adds a penalty stroke and resets,
 * so banking a lead and stalling on pure defense bleeds strokes. Called only while the match
 * clock is still running. Taking an offensive shot resets the clock at the fire site.
 */
function advanceShotClock(side: SideState, dt: number): SideState {
  if (Math.floor(side.energy) < cheapestOffenseCost(side)) return side; // can't attack ⇒ paused
  let shotClock = side.shotClock - dt;
  let score = side.score;
  if (shotClock <= 0) {
    score += SHOT_CLOCK.penaltyStroke;
    shotClock = SHOT_CLOCK.seconds; // reset and keep the pressure on
  }
  return { ...side, shotClock, score };
}

/** Reset a side's shot clock + bank a defense charge — applied when it fires an offensive shot. */
function rewardOffense(side: SideState): SideState {
  return {
    ...side,
    shotClock: SHOT_CLOCK.seconds,
    defenseCharges: Math.min(DEFENSE_CHARGE.max, side.defenseCharges + DEFENSE_CHARGE.perShot),
  };
}

// ---------------------------------------------------------------------------
// Tick: the deterministic heart Agent A owns (§4.2 / §4.4)
// ---------------------------------------------------------------------------

/**
 * Eased speed factor along the meter (§3.4 playtest): 1 at the active sweet spot,
 * `meterSlowFactor` at the extremes (0 and 1), blended with smoothstep so velocity
 * is C1-continuous (no stutter) — and zero-slope at the sweet spot itself, so it
 * reads as a single smooth "fast through the zone, ease at the ends" pass.
 */
function meterSpeedFactor(meter: number, sweet: number): number {
  const t = meter <= sweet ? meter / sweet : (1 - meter) / (1 - sweet); // 0 at extremes, 1 at sweet
  const eased = t * t * (3 - 2 * t); // smoothstep
  return SWING.meterSlowFactor + (1 - SWING.meterSlowFactor) * eased;
}

/**
 * Advance the swing meter for an in-progress swing (§3.4).
 *
 * The meter OSCILLATES continuously, reflecting off both 0 and 1, until the player
 * taps to lock — there is no auto-lock / auto-fire (playtest). Its velocity follows
 * {@link meterSpeedFactor}: PEAK at the phase's sweet spot, eased/slow at the
 * extremes, applied to the active sweet spot only so it never stutters.
 */
export function advanceSwing(swing: SwingState, dt: number): SwingState {
  if (swing.phase !== 'backswing' && swing.phase !== 'impact') return swing;
  const sweet = swing.phase === 'impact' ? SWING.impSweet : SWING.backSweet;
  const peak = swing.phase === 'impact' ? SWING.impactSpeed : SWING.backswingSpeed;
  const speed = peak * meterSpeedFactor(swing.meter, sweet);
  let meter = swing.meter + swing.meterDir * speed * dt;
  let meterDir = swing.meterDir;
  // Reflect at the extremes so the marker bounces 0 ↔ 1 indefinitely.
  if (meter > 1) {
    meter = Math.max(0, 2 - meter);
    meterDir = -1;
  } else if (meter < 0) {
    meter = Math.min(1, -meter);
    meterDir = 1;
  }
  return { ...swing, meter, meterDir };
}

/** Count down hazard lifetimes on a green, dropping any that have expired (§3.7). */
function tickHazards(green: Green, dt: number): Green {
  if (green.hazards.length === 0) return green;
  const hazards = green.hazards
    .map((h) => ({ ...h, remaining: h.remaining - dt }))
    .filter((h) => h.remaining > 0);
  return { ...green, hazards };
}

function tick(state: GameState, dt: number, rng: Rng): GameState {
  if (state.phase === 'final') return state;

  // 1. Clock down (playoff does not run the clock; the endless range freezes it).
  let clock = state.clock;
  if (state.phase === 'playing' && state.mode === 'duel') clock = Math.max(0, state.clock - dt);

  // 2. Energy regen, per side, capped (§3.6). AI uses its own difficulty regen.
  const aiParams = AI_PARAMS[state.ai.difficulty];
  let you = regenEnergy(state.you, dt, ENERGY_REGEN_PER_SECOND, state.maxEnergy);
  let rival = regenEnergy(state.rival, dt, aiParams.energyRegenPerSecond, state.maxEnergy);

  // 3. Per-tee rough timers + hazard lifetimes count down (§3.7).
  you = tickTeeRough({ ...you, green: tickHazards(you.green, dt) }, dt);
  rival = tickTeeRough({ ...rival, green: tickHazards(rival.green, dt) }, dt);

  // 3b. Shot clocks (anti-turtle): only while the match clock is still running. Uses the
  // freshly-regened energy for the affordability gate. A leader who stops attacking to protect
  // a lead bleeds a penalty stroke every SHOT_CLOCK.seconds.
  if (state.phase === 'playing' && clock > 0 && state.mode === 'duel') {
    you = advanceShotClock(you, dt);
    rival = advanceShotClock(rival, dt);
  }

  // 4. Wind gust countdown (§3.7).
  let wind = state.wind;
  if (wind.remaining > 0) {
    const remaining = Math.max(0, wind.remaining - dt);
    wind = remaining === 0 ? { remaining: 0, accel: vec(0, 0) } : { ...wind, remaining };
  }

  // 5. Swing meters advance (§3.4): oscillate until the player taps; no auto-fire. Both sides have
  // their own meter so two humans can swing concurrently (in solo the rival meter stays idle).
  const swing = advanceSwing(state.swing, dt);
  const rivalSwing = advanceSwing(state.rivalSwing, dt);

  let next: GameState = { ...state, clock, you, rival, wind, swing, rivalSwing };

  // 6. Advance balls in flight/rolling, settling + scoring as they come to rest.
  next = stepBalls(next, dt, rng);

  // 7. AI drive (§3.8): the rival is sim-internal — it does NOT go through the
  // human {@link Action} union (PlayOffense/PlayDefense are `you`-only). Instead
  // we run Agent C's pure policy here and apply its decision directly to the
  // rival side, then reset whichever timer was armed so the AI waits a full
  // cooldown rather than re-firing every tick.
  //
  // DETERMINISTIC RNG DRAW ORDER (must stay fixed — identical (state, dt, rng)
  // yields identical output):
  //   1. decideAi  — internally draws OFFENSE first (skip/club/power/aim/shank),
  //                  then DEFENSE (card pick).
  //   2. AI offense apply — createBall scatter draws, then cycleOffenseCard draw.
  //   3. AI defense apply — applyDefense draws, then cycleDefenseCard draw.
  //   4. Timer resets — ATTACK reset draw (if armed), then DEFENSE reset draw.
  // Only when the rival is AI-controlled (solo). In PvP the rival is a human (player 2) who drives
  // `rivalSwing` through the same actions — so we skip the AI entirely.
  if (next.aiRival) {
    next = driveAi(next, dt, rng);
    next = advanceAiTimers(next, dt);
  }

  // 8. End-of-match transition (§3.5 incl. tie-break). The endless range never ends.
  return next.mode === 'range' ? next : resolveClockExpiry(next);
}

/**
 * Step every ball one fixed frame via Agent B's physics; settle + score on rest.
 * TODO(Agent B): stepBall/createBall/scoreShot are throwing stubs until B lands.
 */
function stepBalls(state: GameState, dt: number, rng: Rng): GameState {
  if (state.balls.length === 0) return state;

  let you = state.you;
  let rival = state.rival;
  const nextBalls: Ball[] = [];

  for (const ball of state.balls) {
    // Already at rest: LINGER on screen (settled ~3s) or SINK (water), then remove.
    // It was scored once on the tick it came to rest, so just count down here.
    if (ball.phase === 'settled' || ball.phase === 'sinking') {
      const rem = ball.restRemaining - dt;
      if (rem > 0) nextBalls.push({ ...ball, restRemaining: rem });
      continue;
    }

    const targetGreen = ball.side === 'you' ? rival.green : you.green;
    const stepped = stepBall(ball, targetGreen, state.wind, rng);
    if (stepped.phase !== 'settled') {
      nextBalls.push(stepped);
      continue;
    }
    // GOPHER steal (§3.7): the shot landed, then a gopher snatches it — a forced "double par"
    // penalty (or a lost playoff ball), with its own steal animation during a short linger.
    if (stepped.stolen) {
      if (stepped.playoff) {
        if (ball.side === 'you') you = { ...you, playoffDp: Infinity };
        else rival = { ...rival, playoffDp: Infinity };
      } else if (ball.side === 'you') {
        you = { ...you, score: you.score + GOPHER.penaltyStrokes };
      } else {
        rival = { ...rival, score: rival.score + GOPHER.penaltyStrokes };
      }
      nextBalls.push({ ...stepped, restRemaining: GOPHER.lingerSeconds, scoreLabel: null });
      continue;
    }

    // Just came to rest: score ONCE (or record playoff closeness) against the green.
    let scoreLabel: ScoreResult | null = null;
    if (stepped.playoff) {
      const dp = dist(stepped.pos, targetGreen.pin);
      if (ball.side === 'you') you = { ...you, playoffDp: dp };
      else rival = { ...rival, playoffDp: dp };
    } else {
      // A situational knockout (water splash / tree knockdown) forces the score; otherwise
      // it's classified by where it rests (§3.7 counters / §3.5 zones).
      const result = stepped.forcedOutcome ?? scoreShot(stepped.pos, targetGreen, stepped.zoneScale);
      scoreLabel = result; // drives the floating score popup while the ball lingers
      const delta = STROKE_DELTA[result];
      if (ball.side === 'you') you = { ...you, score: you.score + delta };
      else rival = { ...rival, score: rival.score + delta };
    }
    // Keep it visible: a ball in WATER splashes + sinks; otherwise it lingers.
    const sinks = hazardKindAt(stepped.pos, targetGreen) === 'water';
    nextBalls.push(
      sinks
        ? { ...stepped, phase: 'sinking', restRemaining: BALL_SINK_SECONDS, scoreLabel }
        : { ...stepped, restRemaining: BALL_REST_SECONDS, scoreLabel },
    );
  }

  return { ...state, balls: nextBalls, you, rival };
}

/** The side the AI controls (the human is always `you`). */
const AI_SIDE: Side = 'rival';

/**
 * Run Agent C's pure policy and apply its decision to the rival side inline,
 * then reset whichever timer was armed (§3.8). Bypasses the human Action union:
 * the rival never aims/backswings — the policy hands us a fully-resolved angle +
 * power, so offense goes straight to {@link createBall} (a ONE-SHOT, no meter).
 *
 * RNG draw order is fixed (see the call-site comment in {@link tick}):
 * decideAi → offense apply → defense apply → timer resets (attack then defense).
 * `_dt` is unused (the reducer owns timer COUNTDOWN in advanceAiTimers); the
 * RESET window is `cooldownMin + rng * cooldownRange` from AI_PARAMS.
 */
function driveAi(state: GameState, _dt: number, rng: Rng): GameState {
  // decideAi already guards phase (only 'playing' / 'playoff' act). (rng draw #1)
  const decision = decideAi(state, rng);
  const aiParams = AI_PARAMS[state.ai.difficulty];

  let next = state;

  // --- AI offense: a one-shot rival ball (no two-stage meter). (rng draw #2) ---
  const shot = decision.shot;
  if (shot !== null) {
    const params: ShotParams = { power: shot.power, angle: shot.angle, pushOff: 0 };
    const baseBall = createBall(
      {
        side: AI_SIDE,
        club: shot.club,
        origin: shot.origin,
        params,
        targetGreen: next.you.green,
        playoff: next.phase === 'playoff',
      },
      rng,
    );
    // A pending GOPHER (the human played it) steals the AI's shot too — symmetric (§3.7).
    const ball = next.rival.gopherPending ? { ...baseBall, stolen: true } : baseBall;
    const charged: SideState = rewardOffense({
      ...next.rival,
      energy: Math.max(0, next.rival.energy - shot.club.cost),
      shots: next.rival.shots + 1,
      gopherPending: false,
    });
    next = withSide({ ...next, balls: [...next.balls, ball] }, AI_SIDE, charged);
    // Offense is not cycled — the AI keeps its one-per-distance hand. Firing resets its shot
    // clock + banks a defense charge (same anti-turtle economy as the human).
  }

  // --- AI defense: apply the chosen card's effect + bookkeeping. (rng draw #3) ---
  // Anti-turtle: like the human, the AI must hold a defense charge (earned by attacking) to
  // play defense. Out of charges ⇒ skip the effect (the timer below still resets, so it waits a
  // full cooldown and by then has attacked + re-earned a charge).
  const defense = decision.defense;
  if (defense !== null && next.rival.defenseCharges >= 1) {
    const afterEffect = applyDefense(next, AI_SIDE, defense.card, rng);
    const charged: SideState = {
      ...afterEffect.rival,
      energy: Math.max(0, afterEffect.rival.energy - defense.card.cost),
      defenseCharges: afterEffect.rival.defenseCharges - 1,
    };
    next = withSide(afterEffect, AI_SIDE, charged);
    next = cycleDefenseCard(next, AI_SIDE, defense.cardIdx, rng);
  }

  // --- Timer resets: whenever armed (even on a hesitation/no-afford skip) so the
  // AI waits a FULL cooldown rather than retrying every tick. Attack first, then
  // defense, to keep the draw order fixed. (rng draws #4) ---
  let attackTimer = next.ai.attackTimer;
  let defenseTimer = next.ai.defenseTimer;
  if (attackArmed(next.ai)) {
    attackTimer = aiParams.attackCooldownMin + rng() * aiParams.attackCooldownRange;
  }
  if (defenseArmed(next.ai)) {
    defenseTimer = aiParams.defenseCooldownMin + rng() * aiParams.defenseCooldownRange;
  }
  if (attackTimer !== next.ai.attackTimer || defenseTimer !== next.ai.defenseTimer) {
    next = { ...next, ai: { ...next.ai, attackTimer, defenseTimer } };
  }

  return next;
}

function advanceAiTimers(state: GameState, dt: number): GameState {
  return {
    ...state,
    ai: {
      ...state.ai,
      attackTimer: state.ai.attackTimer - dt,
      defenseTimer: state.ai.defenseTimer - dt,
    },
  };
}

/** Any ball still moving (flight or rolling)? Settled/sinking balls only LINGER on
 *  screen and must not hold up match-end / playoff resolution. */
function hasActiveBall(state: GameState): boolean {
  return state.balls.some((b) => b.phase === 'flight' || b.phase === 'rolling');
}

/** Transition out of `playing` when the clock hits zero (§3.5). */
function resolveClockExpiry(state: GameState): GameState {
  if (state.phase !== 'playing' || state.clock > 0) return state;
  // Wait for any in-flight balls to settle before deciding (lingering ones don't count).
  if (hasActiveBall(state)) return state;

  if (state.you.score === state.rival.score) {
    // Tie → sudden-death playoff (§3.5 tie-break, human-decided).
    return { ...state, phase: 'playoff' };
  }
  const winner: Side = state.you.score < state.rival.score ? 'you' : 'rival';
  return { ...state, phase: 'final', winner };
}

/** Decide a playoff once both sides have logged a closest-to-pin distance (§3.5). */
function resolvePlayoff(state: GameState): GameState {
  if (state.phase !== 'playoff') return state;
  if (hasActiveBall(state)) return state;
  const a = state.you.playoffDp;
  const b = state.rival.playoffDp;
  if (a === null || b === null) return state;
  if (a === b) return { ...state, phase: 'final', winner: 'tie' };
  return { ...state, phase: 'final', winner: a < b ? 'you' : 'rival' };
}

// ---------------------------------------------------------------------------
// Action handlers (§4.4)
// ---------------------------------------------------------------------------

function isMatchActive(state: GameState): boolean {
  return state.phase === 'playing' || state.phase === 'playoff';
}

/** Begin aiming with a selected offense card (§3.4). */
function startAim(state: GameState, side: Side, clubIdx: number): GameState {
  if (!isMatchActive(state)) return state;
  const sw = swingOf(state, side);
  if (sw.phase !== 'idle' && sw.phase !== 'aim') return state;
  const hand = (side === 'you' ? state.you : state.rival).offenseHand;
  if (clubIdx < 0 || clubIdx >= hand.length) return state;
  return withSwing(state, side, { ...idleSwing, phase: 'aim', side, clubIdx });
}

/** Set the aim angle during the aim phase (§3.4). */
function setAim(state: GameState, side: Side, angle: number): GameState {
  const sw = swingOf(state, side);
  if (sw.phase !== 'aim') return state;
  return withSwing(state, side, { ...sw, aimAngle: angle });
}

/** Release the aim drag → begin the backswing meter sweeping up (§3.4). */
function startSwing(state: GameState, side: Side): GameState {
  const sw = swingOf(state, side);
  if (sw.phase !== 'aim' || sw.aimAngle === null) return state;
  return withSwing(state, side, { ...sw, phase: 'backswing', meter: 0, meterDir: 1 });
}

/** Lock the backswing (POWER) → meter falls back down toward impact (§3.4). `meter` is the client's
 *  locked value (its real-time meter); clamped server-side. Omitted ⇒ the server's meter (solo). */
function lockBackswing(state: GameState, side: Side, meter?: number): GameState {
  const sw = swingOf(state, side);
  if (sw.phase !== 'backswing') return state;
  const locked = meter === undefined ? sw.meter : Math.max(0, Math.min(1, meter));
  return withSwing(state, side, { ...sw, phase: 'impact', meterDir: -1, backswingMeter: locked });
}

/** Lock the impact (ACCURACY) → resolve + fire the shot (§3.4). `meter` is the client's locked value
 *  (client prediction); clamped server-side. Omitted ⇒ the server's meter (solo). */
function lockImpact(state: GameState, side: Side, rng: Rng, meter?: number): GameState {
  const swing = swingOf(state, side);
  if (swing.phase !== 'impact' || swing.clubIdx === null || swing.aimAngle === null || swing.backswingMeter === null) {
    return state;
  }
  const sideState = side === 'you' ? state.you : state.rival;
  const club = sideState.offenseHand[swing.clubIdx];
  if (club === undefined) return withSwing(state, side, idleSwing);

  // Must afford the club (§3.6). If not, cancel the swing.
  if (club.cost > Math.floor(sideState.energy)) return withSwing(state, side, idleSwing);

  const tee = sideState.tees[boxForPower(club.power)]; // POWER picks the launch tee
  const rough = tee.roughRemaining > 0; // only THIS launch spot may be roughed (§3.7, per-tee)

  const impactMeter = meter === undefined ? swing.meter : Math.max(0, Math.min(1, meter));
  const swingInput: SwingInput = {
    club,
    aimAngle: swing.aimAngle,
    backswingMeter: swing.backswingMeter,
    impactMeter,
    rough,
  };

  const params = resolveSwing(swingInput, rng);

  const targetGreen = side === 'you' ? state.rival.green : state.you.green;
  // createBall computes carry from the tee's SET range + folds scatter/pushOff into landingOffset (§5).
  const baseBall = createBall(
    {
      side,
      club,
      origin: tee.pos,
      params,
      targetGreen,
      playoff: state.phase === 'playoff',
    },
    rng,
  );
  // A pending GOPHER steals THIS shot (§3.7): it flies + lands normally, then gets snatched.
  const ball = sideState.gopherPending ? { ...baseBall, stolen: true } : baseBall;

  const charged: SideState = rewardOffense({
    ...sideState,
    energy: Math.max(0, sideState.energy - club.cost),
    shots: sideState.shots + 1,
    gopherPending: false, // consumed by firing (whether or not one was pending)
  });

  // Offense clubs are NOT cycled — the side keeps its one-per-distance hand (you can replay a
  // club, energy permitting). Only the swing resets. Firing resets the shot clock + banks a
  // defense charge (rewardOffense) — attacking is what fuels defending.
  const withCharge = withSide({ ...state, balls: [...state.balls, ball] }, side, charged);
  return withSwing(withCharge, side, idleSwing);
}

/** Replace a played defense card from the deck, reshuffling from the side's CHOSEN defense
 *  cards when empty (§3.6) — a side is limited to the cards it picked in the deck builder. */
function cycleDefenseCard(state: GameState, side: Side, idx: number, rng: Rng): GameState {
  const s = side === 'you' ? state.you : state.rival;
  let deck = s.defenseDeck;
  if (deck.length === 0) deck = buildDeck(s.defenseList, rng);
  const rest = deck.slice();
  const drawn = rest.pop();
  if (drawn === undefined) return state;
  const hand = s.defenseHand.map((c, i) => (i === idx ? drawn : c));
  return withSide(state, side, { ...s, defenseHand: hand, defenseDeck: rest });
}

/** Play a defensive card: charge energy, apply its effect (Agent C), cycle the card (§3.7). */
function playDefense(state: GameState, side: Side, cardIdx: number, rng: Rng): GameState {
  if (!isMatchActive(state)) return state;
  const s = side === 'you' ? state.you : state.rival;
  const card = s.defenseHand[cardIdx];
  if (card === undefined) return state;
  if (card.cost > Math.floor(s.energy)) return state;
  // Anti-turtle: defending costs a CHARGE, earned by attacking. No charge ⇒ can't defend yet.
  if (s.defenseCharges < 1) return state;

  const afterEffect = applyDefense(state, side, card, rng);

  const charged = withSide(afterEffect, side, {
    ...(side === 'you' ? afterEffect.you : afterEffect.rival),
    energy: Math.max(0, s.energy - card.cost),
    defenseCharges: s.defenseCharges - 1,
  });
  return cycleDefenseCard(charged, side, cardIdx, rng);
}

// ---------------------------------------------------------------------------
// Reducer entry points
// ---------------------------------------------------------------------------

/** Apply one action (for the acting side) without the post-action playoff/clock resolution sweep.
 *  `actingSide` lets the authoritative match host route each player's input to their own side; it
 *  defaults to `you` so the solo/local path is unchanged. */
function applyAction(state: GameState, action: Action, rng: Rng, actingSide: Side): GameState {
  switch (action.type) {
    case 'Tick':
      return tick(state, action.dt, rng);
    case 'Aim':
      return setAim(state, actingSide, action.angle);
    case 'StartSwing':
      return startSwing(state, actingSide);
    case 'LockBackswing':
      return lockBackswing(state, actingSide, action.meter);
    case 'LockImpact':
      return lockImpact(state, actingSide, rng, action.meter);
    case 'PlayOffense':
      return startAim(state, actingSide, action.cardIdx);
    case 'PlayDefense':
      return playDefense(state, actingSide, action.cardIdx, rng);
    default: {
      // Exhaustiveness guard.
      const _never: never = action;
      return _never;
    }
  }
}

/**
 * Apply one action to the state, returning the next state (§4.4).
 *
 * Pure and deterministic: identical (state, action, rng-sequence) always yields
 * an identical result. The `rng` is the injected {@link createRng} stream.
 */
export function reduce(state: GameState, action: Action, rng: Rng, actingSide: Side = 'you'): GameState {
  const next = applyAction(state, action, rng, actingSide);
  // After any action, give the playoff a chance to resolve (a settled playoff
  // ball may have just logged the second side's distance).
  return resolvePlayoff(next);
}
